import { gtmSendEvent } from "@wojo/analytics";
import { useAuth } from "@wojo/auth";
import { useI18n } from "@wojo/localization";
import { FormSelect, Text } from "@wojo/ui";
import React, { useState } from "react";
import {
    CurrencyCode,
    DomainError,
    useStartQuoteMutation,
    useTripBuilderAddExtensionMutation,
    ValidationError,
} from "../../../../generated/graphql";
import { useTripBuilder } from "../context/TripBuilderContext";
import { useTripBuilderQuote } from "../context/TripBuilderQuoteStepContext";
import { QuoteTravelersSoloModal } from "./QuoteTravelersSoloModal";

export const QuoteTravelers: React.FC = () => {
    const { departureId, departure, gcDeparture, promoCode, campaignCode } =
        useTripBuilder();
    const { draftQuote, setDraftId } = useTripBuilderQuote();
    const { productPage } = useI18n("productPage");
    const { customer } = useAuth();
    const [startQuote, { loading: startingQuote }] = useStartQuoteMutation();
    const [addExtension, { loading: addingExtension }] =
        useTripBuilderAddExtensionMutation();
    const [error, setError] = useState<string | undefined>(undefined);
    const isLoading = startingQuote || addingExtension;

    if (!departure || !departureId) {
        return null;
    }

    const nbAllowedTravelers = Math.min(
        parseInt(productPage.tripBuilder.quote.travelers.limit.l()),
        departure.nbSpotsLeft,
    );
    const nbTravelersOptions = Array.from(
        { length: nbAllowedTravelers },
        (_, index) => ({
            label: (index + 1).toString(),
            value: index + 1,
        }),
    );

    return parseInt(productPage.tripBuilder.quote.travelers.limit.l()) === 1 ? (
        <Text.Body>
            {productPage.tripBuilder.quote.travelers.singlePaxMessage.l()}
        </Text.Body>
    ) : (
        <FormSelect
            name="nbTravelers"
            data-testid="nbTravelers"
            loading={isLoading}
            label={
                <Text.Body variant="2" emphasis="2" tag="span">
                    {productPage.tripBuilder.quote.travelers.heading.l()}
                </Text.Body>
            }
            descriptionText={<QuoteTravelersSoloModal />}
            onChange={(value) => {
                setError(undefined);
                const extensionLineItemCodeToAdd = draftQuote?.order.trips
                    .at(0)
                    ?.lineItems.find(
                        (lineItem) =>
                            lineItem.__typename === "TourExtensionLineItem",
                    )?.lineItemCode;
                const gatewayCode =
                    draftQuote?.order.trips.at(0)?.openDepartingFlightLineItem
                        ?.departureGatewayCode;
                if (value && !startingQuote) {
                    startQuote({
                        variables: {
                            input: {
                                departureId,
                                currency: process.env
                                    .NEXT_PUBLIC_CURRENCY_CODE as CurrencyCode,
                                nbSecondaryTravelers:
                                    value > 1 ? value - 1 : undefined,
                                gcDepartureId: gcDeparture?._id,
                                gatewayCode,
                                promoCode: promoCode || undefined,
                                campaignCode: campaignCode || undefined,
                                ...(!!customer?._id && {
                                    customerId: customer._id,
                                }),
                                applyAdvancedRepeatPromo: true,
                            },
                        },
                        onCompleted: (data) => {
                            if (data.startQuote.__typename === "DraftOrder") {
                                gtmSendEvent({
                                    event: "track",
                                    ga4Tracking: {
                                        ga4_object: "TRIP_BUILDER",
                                        ga4_action: "TRAVELERS_SELECTED",
                                        travelerCount: `${value}`,
                                    },
                                });
                                setDraftId(data.startQuote._id);
                                const extensionIsOnOrder =
                                    data.startQuote.order.trips
                                        .at(0)
                                        ?.lineItems.some(
                                            (lineItem) =>
                                                lineItem.lineItemCode ===
                                                extensionLineItemCodeToAdd,
                                        );
                                if (
                                    extensionLineItemCodeToAdd &&
                                    !extensionIsOnOrder
                                ) {
                                    addExtension({
                                        variables: {
                                            id: data.startQuote._id,
                                            lineItemCode:
                                                extensionLineItemCodeToAdd,
                                        },
                                    });
                                }
                            } else {
                                setError(
                                    (
                                        data.startQuote as
                                            | DomainError
                                            | ValidationError
                                    ).message,
                                );
                            }
                        },
                    });
                }
            }}
            validation={{
                required:
                    productPage.tripBuilder.quote.travelers.errorRequired.l(),
                validate: () => error,
            }}
            items={nbTravelersOptions}
        />
    );
};
